import { useState, useCallback } from "react";
import { request } from "../utils/api";

export const useBilling = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchBills = useCallback(async () => {
    try {
      const response = await request("/billing/bills");
      if (response && response.status === 1) {
        return { success: true, data: response.result || [] };
      }
      return { success: false, data: [] };
    } catch (err) {
      console.error("Failed to fetch bills:", err);
      return { success: false, data: [] };
    }
  }, []);

  const fetchBillById = useCallback(async (billId) => {
    try {
      const response = await request(`/billing/bills/${billId}`);
      if (response && response.status === 1) {
        return { success: true, data: response.result };
      }
      return { success: false, data: null };
    } catch (err) {
      console.error("Failed to fetch bill:", err);
      return { success: false, data: null };
    }
  }, []);

  const fetchPaymentHistory = useCallback(async () => {
    try {
      const response = await request("/billing/payments");
      if (response && response.status === 1) {
        return { success: true, data: response.result || [] };
      }
      return { success: false, data: [] };
    } catch (err) {
      console.error("Failed to fetch payment history:", err);
      return { success: false, data: [] };
    }
  }, []);

  const downloadInvoice = async (billId) => {
    try {
      const response = await request(`/billing/bills/${billId}/invoice`);
      if (response && response.status === 1 && response.result) {
        return { success: true, data: response.result };
      }
      return { success: false, message: "Failed to download invoice" };
    } catch (err) {
      console.error("Failed to download invoice:", err);
      return { success: false, message: err.message || "Failed to download invoice" };
    }
  };

  const initiatePayment = async (billId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await request("/billing/payment/initiate", {
        method: "POST",
        body: JSON.stringify({ billId }),
      });
      if (response && response.status === 1 && response.result) {
        return { success: true, data: response.result };
      }
      return { success: false, message: "Failed to initiate payment" };
    } catch (err) {
      console.error("Failed to initiate payment:", err);
      setError(err.message || "Failed to initiate payment");
      return { success: false, message: err.message };
    } finally {
      setLoading(false);
    }
  };

  const capturePayment = useCallback(async (orderId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await request("/billing/payment/capture", {
        method: "POST",
        body: JSON.stringify({ orderId }),
      });
      if (response && response.status === 1) {
        return { success: true, data: response.result };
      }
      return { success: false, message: response.message || "Payment capture failed" };
    } catch (err) {
      console.error("Failed to capture payment:", err);
      setError(err.message || "Failed to capture payment");
      return { success: false, message: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const cancelPlan = useCallback(async (reason) => {
    setLoading(true);
    setError(null);
    try {
      const response = await request("/billing/plan/cancel", {
        method: "POST",
        body: JSON.stringify({ reason }),
      });
      if (response && response.status === 1) {
        return { success: true, message: "Plan cancelled successfully" };
      }
      return { success: false, message: response.message || "Failed to cancel plan" };
    } catch (err) {
      console.error("Failed to cancel plan:", err);
      setError(err.message || "Failed to cancel plan");
      return { success: false, message: err.message || "Failed to cancel plan" };
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    fetchBills,
    fetchBillById,
    fetchPaymentHistory,
    initiatePayment,
    capturePayment,
    cancelPlan,
    downloadInvoice,
  };
};
